import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import DatePicker from 'components/layout/forms/DatePicker';
import translations from 'services/translations/translations';

import {
  customPeriod,
  fiscalMonth,
  fiscalQuarter,
  fiscalYear,
  fiscalYearOf,
  IReportPeriod,
  lastMonth,
  lastMonths,
  lastWeek,
  thisWeek,
  today,
  yesterday,
} from './reportPeriods';

// The periods of the choice: days and weeks, months back from now, and the fiscal year
const DAY_PERIODS = [
  'today',
  'yesterday',
  'this-week',
  'last-week',
  'last-month',
];
const MONTH_PERIODS = [1, 2, 3, 6, 12, 24];
const QUARTERS = [1, 2, 3, 4];
// The fiscal years to choose from: the current one and the ones before it
const FISCAL_YEARS_BACK = 4;

const periodFor = (key: string, year: number, now: Date): IReportPeriod => {
  if (key === 'today') {
    return today(now);
  }
  if (key === 'yesterday') {
    return yesterday(now);
  }
  if (key === 'this-week') {
    return thisWeek(now);
  }
  if (key === 'last-week') {
    return lastWeek(now);
  }
  if (key === 'last-month') {
    return lastMonth(now);
  }
  if (key.startsWith('months-')) {
    return lastMonths(now, parseInt(key.slice('months-'.length), 10));
  }
  if (key.startsWith('quarter-')) {
    return fiscalQuarter(year, parseInt(key.slice('quarter-'.length), 10));
  }
  if (key.startsWith('month-')) {
    return fiscalMonth(year, parseInt(key.slice('month-'.length), 10));
  }
  return fiscalYear(year);
};

// The months of a fiscal year, October first
const FISCAL_MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);

// A month of the fiscal year shortly, e.g. 10/26 or 01/27
const monthLabel = (year: number, month: number): string => {
  const start = fiscalMonth(year, month).from;
  return `${`${start.getMonth() + 1}`.padStart(
    2,
    '0'
  )}/${`${start.getFullYear()}`.slice(2)}`;
};

// The month of the fiscal year (1-12) a day is in
const fiscalMonthOf = (day: Date): number => ((day.getMonth() + 3) % 12) + 1;

/**
 * The period of a report and the buttons to choose it: days and weeks, months back from now,
 * the fiscal year 1.10.-30.9. and its quarters, and any days from the calendar.
 */
const useReportPeriod = (
  // The id of the date fields, unique on the page
  id: string
): { period: IReportPeriod; picker: JSX.Element } => {
  const { t } = useTranslation([translations.offers]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:dashboard.${key}`, options);

  // The chosen period, e.g. 'months-12', 'this-week', 'fiscal-year' or 'quarter-2', and the
  // fiscal year of the fiscal periods (by the year it starts in)
  const [periodKey, setPeriodKey] = useState('months-12');
  const [year, setYear] = useState(() => fiscalYearOf(new Date()));
  // The days chosen from the calendar, both included
  const [customFrom, setCustomFrom] = useState<Date>(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [customTo, setCustomTo] = useState<Date>(() => new Date());
  const period = useMemo(
    () =>
      periodKey === 'custom'
        ? customPeriod(customFrom, customTo)
        : periodFor(periodKey, year, new Date()),
    [periodKey, year, customFrom, customTo]
  );
  const currentFiscalYear = fiscalYearOf(new Date());
  // The months of the chosen fiscal year in the choice: the current month first in the
  // current fiscal year, the others in their order
  const currentMonth = fiscalMonthOf(new Date());
  const shownMonths =
    year === currentFiscalYear
      ? [
          currentMonth,
          ...FISCAL_MONTHS.filter((month) => month !== currentMonth),
        ]
      : FISCAL_MONTHS;

  const picker = (
    <div className="offer-dashboard-periods">
      <div className="offer-dashboard-segments" role="group">
        {DAY_PERIODS.map((key) => (
          <button
            className={key === periodKey ? 'active' : ''}
            key={key}
            onClick={() => setPeriodKey(key)}
            type="button"
          >
            {tr(`periods.${key}`)}
          </button>
        ))}
      </div>
      <div className="offer-dashboard-segments" role="group">
        {MONTH_PERIODS.map((months) => (
          <button
            className={`months-${months}` === periodKey ? 'active' : ''}
            key={months}
            onClick={() => setPeriodKey(`months-${months}`)}
            type="button"
          >
            {tr('Months', { count: months })}
          </button>
        ))}
      </div>
      {/* The fiscal year 1.10.-30.9. and its quarters */}
      <div className="offer-dashboard-segments" role="group">
        <Input
          aria-label={tr('periods.Fiscal year')}
          bsSize="sm"
          className="offer-dashboard-fiscal-year"
          onChange={(event) => {
            setYear(parseInt(event.target.value, 10));
            if (
              periodKey !== 'fiscal-year' &&
              !periodKey.startsWith('quarter-') &&
              !periodKey.startsWith('month-')
            ) {
              setPeriodKey('fiscal-year');
            }
          }}
          type="select"
          value={year}
        >
          {Array.from(
            { length: FISCAL_YEARS_BACK + 1 },
            (_, index) => currentFiscalYear - index
          ).map((fiscal) => (
            <option key={fiscal} value={fiscal}>
              {tr('periods.Fiscal year label', {
                from: fiscal,
                to: `${fiscal + 1}`.slice(2),
              })}
            </option>
          ))}
        </Input>
        <button
          className={periodKey === 'fiscal-year' ? 'active' : ''}
          onClick={() => setPeriodKey('fiscal-year')}
          type="button"
        >
          {tr('periods.Whole fiscal year')}
        </button>
        {QUARTERS.map((quarter) => (
          <button
            className={`quarter-${quarter}` === periodKey ? 'active' : ''}
            key={quarter}
            onClick={() => setPeriodKey(`quarter-${quarter}`)}
            title={tr(`periods.Quarter months ${quarter}`)}
            type="button"
          >
            {`Q${quarter}`}
          </button>
        ))}
        {/* A month of the fiscal year, the current one marked */}
        <Input
          aria-label={tr('periods.Month')}
          bsSize="sm"
          className={`offer-dashboard-fiscal-month${
            periodKey.startsWith('month-') ? ' active' : ''
          }`}
          onChange={(event) =>
            setPeriodKey(
              event.target.value === '' ? 'fiscal-year' : event.target.value
            )
          }
          type="select"
          value={periodKey.startsWith('month-') ? periodKey : ''}
        >
          <option value="">{tr('periods.Month')}</option>
          {shownMonths.map((month) => (
            <option key={month} value={`month-${month}`}>
              {monthLabel(year, month)}
              {year === currentFiscalYear && month === currentMonth
                ? ` ${tr('periods.Current month')}`
                : ''}
            </option>
          ))}
        </Input>
      </div>
      {/* Any days from the calendar */}
      <div
        className={`offer-dashboard-segments offer-dashboard-custom${
          periodKey === 'custom' ? ' active' : ''
        }`}
        title={tr('periods.Custom info')}
      >
        <span className="offer-dashboard-custom-label">
          {tr('periods.Custom')}
        </span>
        <DatePicker
          endDate={customTo}
          id={`${id}-from`}
          onChange={(date) => {
            if (date instanceof Date) {
              setCustomFrom(date);
              setPeriodKey('custom');
            }
          }}
          selected={customFrom}
          startDate={customFrom}
        />
        <span>–</span>
        <DatePicker
          endDate={customTo}
          id={`${id}-to`}
          minDate={customFrom}
          onChange={(date) => {
            if (date instanceof Date) {
              setCustomTo(date);
              setPeriodKey('custom');
            }
          }}
          popperPlacement="bottom-end"
          selected={customTo}
          startDate={customFrom}
        />
      </div>
    </div>
  );

  return { period, picker };
};

export default useReportPeriod;
